import { useMemo } from "react";
import { errorAt, type AddressDraft, type ErrorMap } from "~/lib/forms";
import { useFormat, useT } from "~/lib/i18n";
import { Field } from "./ui/field";
import { Input } from "./ui/input";
import { Select } from "./ui/select";

/** Markets Phygiatech ships to or buys from; any ISO code is still accepted by the API. */
const COUNTRIES = ["FR", "BE", "LU", "CH", "DE", "ES", "IT", "NL", "GB", "US", "CN"] as const;

interface AddressFieldsProps {
  value: AddressDraft;
  onChange: (value: AddressDraft) => void;
  errors: ErrorMap;
  prefix: string;
}

export function AddressFields({ value, onChange, errors, prefix }: AddressFieldsProps) {
  const t = useT();
  const format = useFormat();
  const countries = useMemo(
    () =>
      COUNTRIES.map((code) => ({ value: code as string, label: format.country(code) })).toSorted((a, b) =>
        a.label.localeCompare(b.label),
      ),
    [format],
  );
  const set = (key: keyof AddressDraft) => (next: string) => onChange({ ...value, [key]: next });
  const err = (key: keyof AddressDraft) => errorAt(t, errors, `${prefix}.${key}`);

  return (
    <div className="grid gap-4 sm:grid-cols-6">
      <Field label={t("common.fields.street")} error={err("line1")} className="sm:col-span-6">
        <Input value={value.line1} onChange={(e) => set("line1")(e.target.value)} autoComplete="address-line1" />
      </Field>
      <Field label={t("common.fields.street2")} optionalLabel={t("common.states.optional")} className="sm:col-span-6">
        <Input value={value.line2} onChange={(e) => set("line2")(e.target.value)} autoComplete="address-line2" />
      </Field>
      <Field label={t("common.fields.postalCode")} error={err("postalCode")} className="sm:col-span-2">
        <Input
          value={value.postalCode}
          onChange={(e) => set("postalCode")(e.target.value)}
          autoComplete="postal-code"
          inputMode="numeric"
        />
      </Field>
      <Field label={t("common.fields.city")} error={err("city")} className="sm:col-span-4">
        <Input value={value.city} onChange={(e) => set("city")(e.target.value)} autoComplete="address-level2" />
      </Field>
      <Field label={t("common.fields.country")} className="sm:col-span-6">
        <Select value={value.country} onValueChange={set("country")} options={countries} />
      </Field>
    </div>
  );
}
